import { useCallback, useState } from 'react'
import api, { getApiError } from '../Rest'
import { useExecuteRecaptcha } from './useExecuteRecaptcha'
import { getAffiliateData, SourceUrlStorage } from '@monorepo/utils'
import { AdCommonEvents, callPixelEvent, LandingType, trackRegistration } from '@monorepo/service'
import type { HttpResponse } from '../Rest'
import type { ApiCore } from '../services'
import { RegisterResponse } from '../types'
import { ErrorMsg } from '../Rest/types'
import { ValidationError } from '../services/ApiCoreService'

const TRACEPARENT_KEY = 'traceparent'

const mapResponse = (
  body: ApiCore.LandingUserRegistrationSchema,
  res: HttpResponse<ApiCore.UserAutologinResponse, unknown>,
): ApiCore.UserAutologinResponse &
  Pick<ApiCore.LandingUserRegistrationSchema, 'language' | 'account_registered_by' | 'bonus_id'> & {
    traceparent?: string
  } => {
  return {
    ...res.data,
    language: body.language,
    traceparent: res.headers.get(TRACEPARENT_KEY) ?? undefined,
    bonus_id: body.bonus_id,
    account_registered_by: body.account_registered_by,
  }
}

interface Props {
  sid: string | null
  bonusId?: number
  promocode?: string
  setDataRegister?: (value: RegisterResponse | null) => void
  registrationType?: 'phone' | 'email'
  landingType?: LandingType
}

export const useRegister = ({
  sid,
  bonusId,
  promocode,
  setDataRegister,
  registrationType,
  landingType = 'registration',
}: Props) => {
  const executeRecaptcha = useExecuteRecaptcha()
  const [commonErrorMsg, setCommonErrorMsg] = useState<string | null>(null)
  const [fieldErrorMsg, setFieldErrorMsg] = useState<{ [key: string]: string | null } | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleError = useCallback((error: ErrorMsg) => {
    if (error) {
      if (typeof error.detail === 'string') setCommonErrorMsg(error.detail)
      if (
        typeof error.detail === 'object' &&
        Array.isArray(error.detail) &&
        error.detail[0] &&
        'loc' in error.detail[0]
      ) {
        const err = error.detail as ValidationError[]
        const responseErrors =
          err?.reduce((prev, value) => {
            if (value.loc?.length) prev = { ...prev, [value.loc[value.loc.length - 1]]: value.msg }
            return prev
          }, {}) || null

        if (responseErrors && Object.keys(responseErrors).length) setFieldErrorMsg(responseErrors)
      }
    }
  }, [])

  const handleSuccess = useCallback(
    (data: RegisterResponse) => {
      trackRegistration('success', registrationType, landingType)

      callPixelEvent({ event: AdCommonEvents.Visit, userId: data.user_id })
      callPixelEvent({ event: AdCommonEvents.LandingPageView, userId: data.user_id })
      callPixelEvent({ event: AdCommonEvents.RegNotFinished, userId: data.user_id })
      callPixelEvent({ event: AdCommonEvents.RegFinished, userId: data.user_id })
      setDataRegister?.(data)
    },
    [setDataRegister, registrationType, landingType],
  )

  const handleRequest = useCallback(
    (data: ApiCore.LandingUserRegistrationSchema) => {
      setIsLoading(true)
      setCommonErrorMsg(null)
      setFieldErrorMsg(null)

      executeRecaptcha('register', (reCaptchaToken) => {
        const body = {
          ...data,
          ...getAffiliateData({ bonusId, promocode, referralCode: data.referral_code }),
        } satisfies ApiCore.LandingUserRegistrationSchema

        const headers: Record<string, string> = {}
        if (reCaptchaToken) headers['x-captcha-token'] = reCaptchaToken
        if (sid) headers['X-fingerprint'] = sid

        api.core
          .publicLandingUserRegistrationCreate(
            body,
            { source_url: SourceUrlStorage.get() },
            {
              headers,
              credentials: 'include',
            },
          )
          .then((res) => mapResponse(body, res))
          .then(handleSuccess)
          .catch((err) => {
            trackRegistration('error', registrationType, landingType)

            handleError(getApiError<ErrorMsg>(err))
          })
          .finally(() => {
            setIsLoading(false)
          })
      })
    },
    [executeRecaptcha, handleError, handleSuccess, setIsLoading, bonusId, promocode, sid],
  )

  return { handleRequest, commonErrorMsg, fieldErrorMsg, isLoading }
}
